import { enableGetSegmentsBatching } from '@gonfalon/dogfood-flags/dogfood-flags';
import { getSegment } from '@gonfalon/openapi/operations/getSegment';
import { getSegments, type GetSegmentsQueryParams } from '@gonfalon/openapi/operations/getSegments';
import { getTopSegmentTags } from '@gonfalon/openapi/operations/getTopSegmentTags';
import type { schemas } from '@gonfalon/openapi/types';
import { createQueryKeys } from '@lukemorales/query-key-factory';

import { createInfiniteQueryOptions } from '../internal/createInfiniteQueryOptions';
import { createQueryOptions } from '../internal/createQueryOptions';
import { getSegmentsTaskRunner } from '../internal/getSegmentsTaskRunner';
import { reactQueryResponseAdapter } from '../internal/reactQueryResponseAdapter';

export const getSegmentsQuery = createQueryOptions(getSegments);
export const getInfiniteSegmentsQuery = createInfiniteQueryOptions(getSegments);

/**
 * @deprecated Use `createQueryOptions` with the OpenAPI operation directly instead.
 * See packages/rest-api/README.md for the recommended pattern.
 */
export const segments = createQueryKeys('segments', {
  list: ({
    projectKey,
    environmentKey,
    params,
    batch = true,
    apiVersion,
  }: {
    projectKey: string;
    environmentKey: string;
    params?: GetSegmentsQueryParams;
    batch?: boolean;
    apiVersion?: string;
  }) => ({
    queryKey: [projectKey, environmentKey, params],
    // enableGetSegmentsBatching is a feature flag tied to escalation SC-233978
    queryFn: async () =>
      enableGetSegmentsBatching() && batch
        ? getSegmentsTaskRunner.run({ projectKey, environmentKey, params, apiVersion })
        : reactQueryResponseAdapter(getSegments({ projectKey, environmentKey, query: params, apiVersion })),
    contextQueries: {
      topTags: () => ({
        queryKey: ['topTags', projectKey, environmentKey],
        queryFn: async ({ signal }) =>
          reactQueryResponseAdapter(getTopSegmentTags({ projectKey, environmentKey }, { signal })),
      }),
    },
  }),
  detail: ({
    projectKey,
    environmentKey,
    segmentKey,
    shouldIgnoreErrors,
  }: {
    projectKey: string;
    environmentKey: string;
    segmentKey: string;
    shouldIgnoreErrors?: boolean;
  }) => ({
    queryKey: [projectKey, environmentKey, segmentKey],
    queryFn: async ({ signal }): Promise<schemas['Segment']> => {
      try {
        const result = await reactQueryResponseAdapter(
          getSegment({ projectKey, environmentKey, segmentKey }, { signal }),
        );
        return result;
      } catch (error) {
        if (shouldIgnoreErrors) {
          return new Promise((resolve) => resolve(null as unknown as schemas['Segment']));
        }

        throw error;
      }
    },
  }),
});

export const segmentList = segments.list;
